import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Loader, EmptyState } from "@/components/States";
import { Search } from "lucide-react";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("category") || "all";

  useEffect(() => {
    axios.get(`${API}/categories`).then((c) => setCategories(c.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/products`, { params: { category: activeCat } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [activeCat]);

  const setCat = (slug) => {
    if (slug === "all") setSearchParams({});
    else setSearchParams({ category: slug });
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="animate-fade-in">
      <div className="bg-brand-gold-light/40 py-12">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-sm uppercase tracking-[0.2em] text-brand-gold">Our Menu</p>
          <h1 className="mt-2 font-heading text-4xl text-brand-dark sm:text-5xl">Shop Homemade Delights</h1>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Search */}
        <div className="relative mx-auto mb-8 max-w-xl">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-muted" />
          <input
            data-testid="shop-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search for chocolates, laddus, cookies..."
            className="w-full rounded-full border border-brand-line bg-white py-3 pl-12 pr-4 text-brand-dark outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-brand-gold"
          />
        </div>

        {/* Category filter */}
        <div className="mb-10 flex flex-wrap justify-center gap-2">
          <button
            data-testid="filter-all"
            onClick={() => setCat("all")}
            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
              activeCat === "all" ? "bg-brand text-white" : "border border-brand-line bg-white text-brand-dark hover:bg-brand-gold-light"
            }`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.slug}`}
              onClick={() => setCat(c.slug)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                activeCat === c.slug ? "bg-brand text-white" : "border border-brand-line bg-white text-brand-dark hover:bg-brand-gold-light"
              }`}
            >
              {c.emoji} {c.name}
            </button>
          ))}
        </div>

        {loading ? (
          <Loader />
        ) : filtered.length === 0 ? (
          <EmptyState title="No products found" subtitle="Try a different search or category." />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
